import { CURRENT_TAX_YEAR } from '@/constants';
import { addOrEditItem, deleteItem, getItems } from '@/store/api';

export default {
    namespaced: true,
    state: {
        currentTaxSeason: null,
        intakeDataById: {},
    },
    mutations: {
        updateCurrentTaxSeason(state, taxSeason) {
            state.currentTaxSeason = taxSeason;
            if (taxSeason?.intake_data) {
                const indexed = {};
                taxSeason.intake_data.forEach(intakeData => {
                    indexed[intakeData.intake.id] = intakeData;
                });
                state.intakeDataById = indexed;
            }
        },
        clearCurrentTaxSeason(state) {
            state.currentTaxSeason = null;
            state.intakeDataById = {};
        },
        updateIntakeData(state, intakeData) {
            if (intakeData?.intake) {
                state.intakeDataById[intakeData.intake.id] = intakeData;
            }
        },
    },
    getters: {
        currentTaxSeason: state => state.currentTaxSeason,
        intakeDataList: state => state.currentTaxSeason?.intake_data || [],
        taxSeasonIntakes: state => (state.currentTaxSeason?.intake_data || []).map(d => d.intake),
        getIntakeData: state => intakeId => state.intakeDataById[intakeId] || null,
        isAddressConfirmed: state => state.currentTaxSeason?.address_confirmed || false,
        isPhoneConfirmed: state => state.currentTaxSeason?.phone_confirmed || false,
        isMaritalStatusConfirmed: state => state.currentTaxSeason?.marital_status_confirmed || false,
        allFieldsConfirmed: state => state.currentTaxSeason?.all_fields_confirmed || false,

        // Data phase status
        isDataSubmitted: state => state.currentTaxSeason?.status === 'data_submitted',
        isDataEditable: state => {
            const status = state.currentTaxSeason?.status;
            return !status || status === 'not_started' || status === 'in_progress';
        },
        // True when status is one of: data_submitted, documents_in_progress, or completed
        isPostDataSubmission: state => {
            const status = state.currentTaxSeason?.status;
            return ['data_submitted', 'documents_in_progress', 'completed'].includes(status);
        },

        // Document phase status
        isDocumentsInProgress: state => state.currentTaxSeason?.status === 'documents_in_progress',
        isCompleted: state => state.currentTaxSeason?.status === 'completed',
        // Alias for backward compatibility
        isDocumentsSubmitted: state => state.currentTaxSeason?.status === 'completed',
        // Document phase opens on January 1st of the year following the tax year
        isDocumentPhaseOpen: () => {
            const documentPhaseStartDate = new Date(CURRENT_TAX_YEAR + 1, 0, 1); // January 1st of next year
            return new Date() >= documentPhaseStartDate;
        },
        isDocumentsAccessible: (state, getters) => {
            const status = state.currentTaxSeason?.status;
            const validStatuses = ['data_submitted', 'documents_in_progress', 'completed'];
            const hasCorrectStatus = validStatuses.includes(status);
            return hasCorrectStatus && getters.isDocumentPhaseOpen;
        },
        documentsProgress: state => state.currentTaxSeason?.documents_progress || 0,
        // Check if all intakes have skip_document_collection=true (no documents needed)
        allIntakesSkipped: state => {
            const intakeData = state.currentTaxSeason?.intake_data || [];
            return intakeData.length > 0 && intakeData.every(d => d.skip_document_collection);
        },

        // Backward compatibility aliases
        isSubmitted: state => state.currentTaxSeason?.status === 'data_submitted',
        isEditable: state => {
            const status = state.currentTaxSeason?.status;
            return !status || status === 'not_started' || status === 'in_progress';
        },
    },
    actions: {
        // Tax Season CRUD
        getTaxSeason: getItems('tax-seasons/'),
        createTaxSeason: addOrEditItem('tax-seasons/'),
        updateTaxSeason: addOrEditItem('tax-seasons/'),
        deleteTaxSeason: deleteItem('tax-seasons/'),

        // Intake Data (nested: tax-seasons/{additional_id}/intakes/{id}/)
        getIntakeData: getItems('intakes/', 'tax-seasons/'),
        updateIntakeData: addOrEditItem('intakes/', 'tax-seasons/'),

        // Documents (3-level nesting requires custom functions)
        async getIntakeDocuments({ dispatch, rootGetters }, { tax_year, intake_id }) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/documents/`;
            return await getItems(path)({ dispatch, rootGetters }, {});
        },
        async createIntakeDocument({ dispatch, rootGetters }, { tax_year, intake_id, ...payload }) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/documents/`;
            return await addOrEditItem(path)({ dispatch, rootGetters }, payload);
        },
        async deleteIntakeDocument({ dispatch, rootGetters }, { tax_year, intake_id, doc_id }) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/documents/`;
            return await deleteItem(path)({ dispatch, rootGetters }, { id: doc_id });
        },

        // Fields (4-level nesting requires custom functions)
        async updateDataField({ dispatch, rootGetters }, { tax_year, intake_id, group_id, field_id, ...payload }) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/groups/${group_id}/fields/`;
            return await addOrEditItem(path)({ dispatch, rootGetters }, { id: field_id, ...payload });
        },

        // Business Logic
        async fetchTaxSeason({ commit, dispatch }, { tax_year }) {
            const data = await dispatch('getTaxSeason', { id: tax_year });
            commit(data ? 'updateCurrentTaxSeason' : 'clearCurrentTaxSeason', data);
            return data;
        },

        async fetchAndUpdateIntakeData({ commit, dispatch }, { tax_year, intake_id }) {
            const data = await dispatch('getIntakeData', { additional_id: tax_year, id: intake_id });
            if (data) commit('updateIntakeData', data);
            return data;
        },

        async saveIntakeData({ commit, dispatch }, { tax_year, intake_id, ...payload }) {
            const data = await dispatch('updateIntakeData', { additional_id: tax_year, id: intake_id, ...payload });
            if (data) commit('updateIntakeData', data);
            return data;
        },

        async getCurrentOrCreateTaxSeason({ commit, dispatch }) {
            let taxSeason = await dispatch('getTaxSeason', { id: CURRENT_TAX_YEAR });
            if (!taxSeason) {
                taxSeason = await dispatch('createTaxSeason', { tax_year: CURRENT_TAX_YEAR });
            }
            if (taxSeason) commit('updateCurrentTaxSeason', taxSeason);
            return taxSeason;
        },

        async confirmAddress({ commit, dispatch, getters }, { tax_year, addressData }) {
            const updates = { id: tax_year, address_confirmed: true, ...addressData };
            if (getters.isEditable) updates.status = 'in_progress';
            const data = await dispatch('updateTaxSeason', updates);
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        async confirmPhone({ commit, dispatch, getters }, { tax_year, phone }) {
            const updates = { id: tax_year, phone_confirmed: true, phone };
            if (getters.isEditable) updates.status = 'in_progress';
            const data = await dispatch('updateTaxSeason', updates);
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        async confirmMaritalStatus({ commit, dispatch, getters }, { tax_year, filing_status }) {
            const updates = { id: tax_year, marital_status_confirmed: true, filing_status };
            if (getters.isEditable) updates.status = 'in_progress';
            const data = await dispatch('updateTaxSeason', updates);
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        async submitTaxSeason({ dispatch }, { tax_year }) {
            // Deprecated: Use submitData instead
            return dispatch('submitData', { tax_year });
        },

        async submitData({ commit, dispatch }, { tax_year }) {
            const data = await dispatch('updateTaxSeason', { id: tax_year, status: 'data_submitted' });
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        async submitDocuments({ commit, dispatch }, { tax_year }) {
            const data = await dispatch('updateTaxSeason', { id: tax_year, status: 'completed' });
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        // ============================================
        // ADVISOR-SPECIFIC ACTIONS
        // These actions allow advisors to access/edit client tax season data by passing client_id
        // ============================================

        // Fetch client tax season (lightweight - for dashboard)
        async fetchAdvisorClientTaxSeasonStatus({ dispatch }, { client_id, tax_year }) {
            try {
                const data = await dispatch('getTaxSeason', {
                    id: tax_year,
                    user_id: client_id,
                });
                return data;
            } catch (e) {
                if (e.response?.status === 404) {
                    return null; // No tax season data yet
                }
                throw e;
            }
        },

        // Fetch full client tax season (for detail page)
        async fetchAdvisorClientTaxSeason({ commit, dispatch }, { client_id, tax_year }) {
            const data = await dispatch('getTaxSeason', {
                id: tax_year,
                user_id: client_id,
            });
            commit(data ? 'updateCurrentTaxSeason' : 'clearCurrentTaxSeason', data);
            return data;
        },

        // Confirm client address (advisor context)
        async confirmAdvisorClientAddress({ commit, dispatch, getters }, { client_id, tax_year, addressData }) {
            const updates = {
                id: tax_year,
                user_id: client_id,
                address_confirmed: true,
                ...addressData,
            };
            if (getters.isEditable) updates.status = 'in_progress';
            const data = await dispatch('updateTaxSeason', updates);
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        // Confirm client phone (advisor context)
        async confirmAdvisorClientPhone({ commit, dispatch, getters }, { client_id, tax_year, phone }) {
            const updates = {
                id: tax_year,
                user_id: client_id,
                phone_confirmed: true,
                phone,
            };
            if (getters.isEditable) updates.status = 'in_progress';
            const data = await dispatch('updateTaxSeason', updates);
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        // Confirm client marital status (advisor context)
        async confirmAdvisorClientMaritalStatus({ commit, dispatch, getters }, { client_id, tax_year, filing_status }) {
            const updates = {
                id: tax_year,
                user_id: client_id,
                marital_status_confirmed: true,
                filing_status,
            };
            if (getters.isEditable) updates.status = 'in_progress';
            const data = await dispatch('updateTaxSeason', updates);
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        // Submit client tax season (advisor context)
        async submitAdvisorClientTaxSeason({ commit, dispatch }, { client_id, tax_year }) {
            const data = await dispatch('updateTaxSeason', {
                id: tax_year,
                user_id: client_id,
                status: 'data_submitted',
            });
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        // Submit client documents (advisor context)
        async submitAdvisorClientDocuments({ commit, dispatch }, { client_id, tax_year }) {
            const data = await dispatch('updateTaxSeason', {
                id: tax_year,
                user_id: client_id,
                status: 'completed',
            });
            if (data) commit('updateCurrentTaxSeason', data);
            return data;
        },

        // Fetch and update client intake data (advisor context)
        async fetchAdvisorClientIntakeData({ commit, dispatch }, { client_id, tax_year, intake_id }) {
            const data = await dispatch('getIntakeData', {
                additional_id: tax_year,
                id: intake_id,
                user_id: client_id,
            });
            if (data) commit('updateIntakeData', data);
            return data;
        },

        // Save client intake data (advisor context)
        async saveAdvisorClientIntakeData({ commit, dispatch }, { client_id, tax_year, intake_id, ...payload }) {
            const data = await dispatch('updateIntakeData', {
                additional_id: tax_year,
                id: intake_id,
                user_id: client_id,
                ...payload,
            });
            if (data) commit('updateIntakeData', data);
            return data;
        },

        // Update client data field (advisor context)
        async updateAdvisorClientDataField(
            { dispatch, rootGetters },
            { client_id, tax_year, intake_id, group_id, field_id, ...payload },
        ) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/groups/${group_id}/fields/`;
            return await addOrEditItem(path)(
                { dispatch, rootGetters },
                {
                    id: field_id,
                    user_id: client_id,
                    ...payload,
                },
            );
        },

        // Get client intake documents (advisor context)
        async getAdvisorClientIntakeDocuments({ dispatch, rootGetters }, { client_id, tax_year, intake_id }) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/documents/`;
            return await getItems(path)({ dispatch, rootGetters }, { user_id: client_id });
        },

        // Create client intake document (advisor context)
        async createAdvisorClientIntakeDocument(
            { dispatch, rootGetters },
            { client_id, tax_year, intake_id, ...payload },
        ) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/documents/`;
            return await addOrEditItem(path)(
                { dispatch, rootGetters },
                {
                    user_id: client_id,
                    ...payload,
                },
            );
        },

        // Delete client intake document (advisor context)
        async deleteAdvisorClientIntakeDocument({ dispatch, rootGetters }, { client_id, tax_year, intake_id, doc_id }) {
            const path = `tax-seasons/${tax_year}/intakes/${intake_id}/documents/`;
            return await deleteItem(path)(
                { dispatch, rootGetters },
                {
                    id: doc_id,
                    user_id: client_id,
                },
            );
        },
    },
};
